import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Stack,
    Paper,
    Avatar,
    LinearProgress,
    Alert,
    Snackbar,
    Tooltip,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    IconPlus,
    IconEdit,
    IconTrash,
    IconCalendar,
    IconClock,
    IconMapPin,
    IconUsers,
    IconPalette,
    IconInfoCircle,
    IconChevronDown,
    IconEye,
} from '@tabler/icons';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import { Helmet } from 'react-helmet';
import MainCard from 'ui-component/cards/MainCard';
import AnimateButton from 'ui-component/extended/AnimateButton';

// GraphQL Queries and Mutations
const GET_THEMES = gql`
    query GetThemes($eventId: String!) {
        themes(eventId: $eventId) {
            id
            name
            description
            type
            workshopDates {
                id
                eventDate
                startTime
                endTime
                venue
                allowedRegistrations
                types {
                    type_label
                    allowed_registrations
                }
            }
            createdAt
            updatedAt
        }
    }
`;

const CREATE_THEME = gql`
    mutation CreateTheme($input: CreateThemeInput!) {
        createTheme(input: $input) {
            id
            name
            description
            type
            workshopDates {
                id
                eventDate
                startTime
                endTime
                venue
                allowedRegistrations
                types {
                    type_label
                    allowed_registrations
                }
            }
        }
    }
`;

const UPDATE_THEME = gql`
    mutation UpdateTheme($id: ID!, $input: UpdateThemeInput!) {
        updateTheme(id: $id, input: $input) {
            id
            name
            description
            type
            workshopDates {
                id
                eventDate
                startTime
                endTime
                venue
                allowedRegistrations
                types {
                    type_label
                    allowed_registrations
                }
            }
        }
    }
`;

const DELETE_THEME = gql`
    mutation DeleteTheme($id: ID!, $reason: String!, $eventId: String!) {
        deleteTheme(id: $id, reason: $reason, eventId: $eventId) {
            id
            success
            message
        }
    }
`;

// Theme Card Component
const ThemeCard = ({ theme, onEdit, onDelete, onView }) => {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteReason, setDeleteReason] = useState('');

    const handleDelete = () => {
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        onDelete(theme.id, deleteReason);
        setDeleteDialogOpen(false);
        setDeleteReason('');
    };

    const formatDate = dateString => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatTime = timeString => {
        return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <>
            <Card
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    },
                }}
            >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar
                            sx={{
                                bgcolor: 'primary.main',
                                mr: 2,
                                width: 48,
                                height: 48,
                            }}
                        >
                            <IconPalette size={24} />
                        </Avatar>
                        <Box>
                            <Typography variant='h6' component='h3' gutterBottom>
                                {theme.name}
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                                {theme.description || 'No description provided'}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant='subtitle2' gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                        Workshop Dates ({theme.workshopDates?.length || 0})
                    </Typography>

                    {theme.workshopDates?.length > 0 ? (
                        <Stack spacing={1}>
                            {theme.workshopDates.slice(0, 3).map((workshop, index) => (
                                <Paper
                                    key={index}
                                    sx={{
                                        p: 1.5,
                                        bgcolor: 'grey.50',
                                        border: '1px solid',
                                        borderColor: 'grey.200',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        <IconCalendar size={16} style={{ marginRight: 8 }} />
                                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                                            {formatDate(workshop.eventDate)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        <IconClock size={16} style={{ marginRight: 8 }} />
                                        <Typography variant='body2'>
                                            {formatTime(workshop.startTime)} - {formatTime(workshop.endTime)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        <IconMapPin size={16} style={{ marginRight: 8 }} />
                                        <Typography variant='body2'>{workshop.venue}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <IconUsers size={16} style={{ marginRight: 8 }} />
                                        <Typography variant='body2'>
                                            Max {workshop.allowedRegistrations} registrations
                                        </Typography>
                                    </Box>
                                </Paper>
                            ))}
                            {theme.workshopDates.length > 3 && (
                                <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center' }}>
                                    +{theme.workshopDates.length - 3} more dates
                                </Typography>
                            )}
                        </Stack>
                    ) : (
                        <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
                            No workshop dates scheduled
                        </Typography>
                    )}
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                        size='small'
                        startIcon={<IconEye size={16} />}
                        onClick={() => onView(theme)}
                        sx={{ mr: 1 }}
                        color='primary'
                    >
                        View
                    </Button>
                    <Button
                        size='small'
                        startIcon={<IconEdit size={16} />}
                        onClick={() => onEdit(theme)}
                        sx={{ mr: 1 }}
                    >
                        Edit
                    </Button>
                    <Button size='small' color='error' startIcon={<IconTrash size={16} />} onClick={handleDelete}>
                        Delete
                    </Button>
                </CardActions>
            </Card>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth='sm' fullWidth>
                <DialogTitle>Delete Theme</DialogTitle>
                <DialogContent>
                    <Typography variant='body2' sx={{ mb: 2 }}>
                        Are you sure you want to delete "{theme.name}"? This action cannot be undone.
                    </Typography>
                    <TextField
                        fullWidth
                        label='Reason for deletion'
                        value={deleteReason}
                        onChange={e => setDeleteReason(e.target.value)}
                        multiline
                        rows={3}
                        required
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={confirmDelete} color='error' disabled={!deleteReason.trim()}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Add/Edit Theme Modal Component
const ThemeModal = ({ open, onClose, theme, onSave }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: '',
        workshopDates: [],
    });

    const [errors, setErrors] = useState({});
    const [expandedAccordions, setExpandedAccordions] = useState({});
    const [instantValidation, setInstantValidation] = useState({});

    useEffect(() => {
        if (theme) {
            setFormData({
                name: theme.name || '',
                description: theme.description || '',
                type: theme.type || '',
                workshopDates: (theme.workshopDates || []).map(date => ({
                    ...date,
                    id: date.id || `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Ensure ID exists
                    allowedRegistrations:
                        date.allowedRegistrations === undefined ||
                        date.allowedRegistrations === null ||
                        date.allowedRegistrations === 0
                            ? ''
                            : date.allowedRegistrations, // Convert to empty string if not set
                    types: date.types || [], // Initialize types array
                })),
            });
        } else {
            setFormData({
                name: '',
                description: '',
                type: '',
                workshopDates: [],
            });
        }
        setErrors({});
        setInstantValidation({});
        setExpandedAccordions({});
    }, [theme, open]);

    const addWorkshopDate = () => {
        const newIndex = formData.workshopDates.length;
        setFormData({
            ...formData,
            workshopDates: [
                ...formData.workshopDates,
                {
                    id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Generate unique ID
                    eventDate: '',
                    startTime: '',
                    endTime: '',
                    venue: '',
                    allowedRegistrations: '', // Empty by default
                    types: [], // Initialize empty types array
                },
            ],
        });
        // Auto-expand the new accordion
        setExpandedAccordions(prev => ({
            ...prev,
            [newIndex]: true,
        }));
    };

    const updateWorkshopDate = (index, field, value) => {
        const updatedDates = [...formData.workshopDates];
        updatedDates[index] = { ...updatedDates[index], [field]: value };
        setFormData({ ...formData, workshopDates: updatedDates });

        // Instant validation for time fields
        if (field === 'startTime' || field === 'endTime') {
            validateTimeFields(updatedDates, index);
        }

        // Instant validation for duplicate dates
        if (field === 'eventDate') {
            validateDuplicateDates(updatedDates, index);
        }
    };

    const addWorkshopType = workshopIndex => {
        const updatedDates = [...formData.workshopDates];
        const currentTypes = updatedDates[workshopIndex].types || [];
        updatedDates[workshopIndex] = {
            ...updatedDates[workshopIndex],
            types: [
                ...currentTypes,
                {
                    type_label: '',
                    allowed_registrations: '',
                },
            ],
        };
        setFormData({ ...formData, workshopDates: updatedDates });
    };

    const updateWorkshopType = (workshopIndex, typeIndex, field, value) => {
        const updatedDates = [...formData.workshopDates];
        const currentTypes = [...(updatedDates[workshopIndex].types || [])];
        currentTypes[typeIndex] = {
            ...currentTypes[typeIndex],
            [field]: value,
        };
        updatedDates[workshopIndex] = {
            ...updatedDates[workshopIndex],
            types: currentTypes,
        };
        setFormData({ ...formData, workshopDates: updatedDates });
    };

    const removeWorkshopType = (workshopIndex, typeIndex) => {
        const updatedDates = [...formData.workshopDates];
        const currentTypes = [...(updatedDates[workshopIndex].types || [])];
        currentTypes.splice(typeIndex, 1);
        updatedDates[workshopIndex] = {
            ...updatedDates[workshopIndex],
            types: currentTypes,
        };
        setFormData({ ...formData, workshopDates: updatedDates });
    };

    const validateTimeFields = (dates, currentIndex) => {
        const currentDate = dates[currentIndex];
        if (currentDate.startTime && currentDate.endTime) {
            const newInstantValidation = { ...instantValidation };
            if (currentDate.startTime >= currentDate.endTime) {
                newInstantValidation[`${currentIndex}_endTime`] = 'End time must be after start time';
            } else {
                delete newInstantValidation[`${currentIndex}_endTime`];
            }
            setInstantValidation(newInstantValidation);
        }
    };

    const validateDuplicateDates = (dates, currentIndex) => {
        const currentDate = dates[currentIndex];
        if (currentDate.eventDate) {
            const newInstantValidation = { ...instantValidation };
            const duplicateIndex = dates.findIndex(
                (date, index) => index !== currentIndex && date.eventDate === currentDate.eventDate
            );

            if (duplicateIndex !== -1) {
                newInstantValidation[`${currentIndex}_eventDate`] = 'This date is already used';
            } else {
                delete newInstantValidation[`${currentIndex}_eventDate`];
            }
            setInstantValidation(newInstantValidation);
        }
    };

    const removeWorkshopDate = index => {
        const updatedDates = formData.workshopDates.filter((_, i) => i !== index);
        setFormData({ ...formData, workshopDates: updatedDates });

        // Clean up instant validation for removed date
        const newInstantValidation = { ...instantValidation };
        Object.keys(newInstantValidation).forEach(key => {
            if (key.startsWith(`${index}_`)) {
                delete newInstantValidation[key];
            }
        });
        setInstantValidation(newInstantValidation);

        // Clean up accordion state for removed date
        const newExpandedAccordions = { ...expandedAccordions };
        delete newExpandedAccordions[index];
        // Shift down the indices for accordions after the removed one
        const shiftedAccordions = {};
        Object.keys(newExpandedAccordions).forEach(key => {
            const keyIndex = parseInt(key);
            if (keyIndex > index) {
                shiftedAccordions[keyIndex - 1] = newExpandedAccordions[key];
            } else if (keyIndex < index) {
                shiftedAccordions[keyIndex] = newExpandedAccordions[key];
            }
        });
        setExpandedAccordions(shiftedAccordions);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Theme name is required';
        }

        // Validate workshop dates
        const dateErrors = {};
        formData.workshopDates.forEach((date, index) => {
            const dateError = {};

            if (!date.eventDate) {
                dateError.eventDate = 'Event date is required';
            }

            if (!date.startTime) {
                dateError.startTime = 'Start time is required';
            }

            if (!date.endTime) {
                dateError.endTime = 'End time is required';
            }

            if (date.startTime && date.endTime && date.startTime >= date.endTime) {
                dateError.endTime = 'End time must be after start time';
            }

            if (!date.venue.trim()) {
                dateError.venue = 'Venue is required';
            }

            if (date.allowedRegistrations !== '' && (!date.allowedRegistrations || date.allowedRegistrations <= 0)) {
                dateError.allowedRegistrations = 'Allowed registrations must be greater than 0';
            }

            // Validate types if they exist
            if (date.types && date.types.length > 0) {
                const typesError = {};

                // Check if types sum exceeds workshop allowedRegistrations
                const typesSum = date.types.reduce((sum, type) => {
                    const allowedReg = parseInt(type.allowed_registrations) || 0;
                    return sum + allowedReg;
                }, 0);

                if (date.allowedRegistrations && typesSum > date.allowedRegistrations) {
                    typesError.sum = `Total types registrations (${typesSum}) cannot exceed workshop allowed registrations (${date.allowedRegistrations})`;
                }

                // Validate individual types
                date.types.forEach((type, typeIndex) => {
                    if (!type.type_label.trim()) {
                        typesError[`type_${typeIndex}_label`] = 'Type label is required';
                    }
                    if (
                        type.allowed_registrations !== '' &&
                        (!type.allowed_registrations || type.allowed_registrations <= 0)
                    ) {
                        typesError[`type_${typeIndex}_registrations`] = 'Type registrations must be greater than 0';
                    }
                });

                if (Object.keys(typesError).length > 0) {
                    dateError.types = typesError;
                }
            }

            if (Object.keys(dateError).length > 0) {
                dateErrors[index] = dateError;
            }
        });

        // Check for duplicate dates
        const dates = formData.workshopDates.map(d => d.eventDate).filter(Boolean);
        const duplicateDates = dates.filter((date, index) => dates.indexOf(date) !== index);
        if (duplicateDates.length > 0) {
            formData.workshopDates.forEach((date, index) => {
                if (duplicateDates.includes(date.eventDate)) {
                    if (!dateErrors[index]) dateErrors[index] = {};
                    dateErrors[index].eventDate = 'This date is already used';
                }
            });
        }

        if (Object.keys(dateErrors).length > 0) {
            newErrors.workshopDates = dateErrors;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (validateForm()) {
            // Clean workshop dates data before sending (preserve existing IDs)
            const cleanWorkshopDates = formData.workshopDates.map(date => ({
                id: date.id, // Preserve existing workshop date ID
                eventDate: date.eventDate,
                startTime: date.startTime,
                endTime: date.endTime,
                venue: date.venue,
                allowedRegistrations: date.allowedRegistrations,
                types: (date.types || []).map(type => ({
                    type_label: type.type_label,
                    allowed_registrations: type.allowed_registrations,
                })), // Clean types array - remove __typename
            }));

            onSave({
                ...formData,
                workshopDates: cleanWorkshopDates,
            });
        }
    };

    const handleAccordionChange = index => (event, isExpanded) => {
        setExpandedAccordions(prev => ({
            ...prev,
            [index]: isExpanded,
        }));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <IconPalette size={24} />
                {theme ? 'Edit Theme' : 'Create New Theme'}
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {/* Sticky Header */}
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: 'white',
                        borderBottom: '1px solid #e0e0e0',
                        p: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant='h6' sx={{ fontWeight: 600 }}>
                        Workshop Dates ({formData.workshopDates.length})
                    </Typography>
                    <Button
                        startIcon={<IconPlus size={16} />}
                        onClick={addWorkshopDate}
                        variant='outlined'
                        size='small'
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            border: 'none',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                            },
                        }}
                    >
                        Add Date
                    </Button>
                </Box>

                <Box sx={{ p: 3 }}>
                    <TextField
                        fullWidth
                        label='Theme Name'
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        error={!!errors.name}
                        helperText={errors.name}
                        sx={{ mb: 3 }}
                    />

                    <TextField
                        fullWidth
                        label='Description (Optional)'
                        value={formData.description}
                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                        multiline
                        rows={3}
                        sx={{ mb: 3 }}
                    />
                    {/* 
                    <TextField
                        fullWidth
                        label='Type (Optional)'
                        value={formData.type}
                        onChange={e => setFormData({ ...formData, type: e.target.value })}
                        multiline
                        rows={2}
                        sx={{ mb: 3 }}
                    /> */}

                    {formData.workshopDates.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <IconCalendar size={48} style={{ color: '#ccc', marginBottom: 16 }} />
                            <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
                                No workshop dates added yet. Click "Add Date" to get started.
                            </Typography>
                            <Button
                                startIcon={<IconPlus size={16} />}
                                onClick={addWorkshopDate}
                                variant='contained'
                                sx={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                    },
                                }}
                            >
                                Add First Date
                            </Button>
                        </Box>
                    )}

                    {formData.workshopDates.map((workshop, index) => (
                        <Accordion
                            key={index}
                            expanded={expandedAccordions[index] || false}
                            onChange={handleAccordionChange(index)}
                            sx={{
                                mb: 2,
                                '&:before': {
                                    display: 'none',
                                },
                                '&.Mui-expanded': {
                                    margin: '0 0 16px 0',
                                },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<IconChevronDown size={20} />}
                                sx={{
                                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                    borderRadius: '8px 8px 0 0',
                                    minHeight: 56,
                                    '&.Mui-expanded': {
                                        minHeight: 56,
                                    },
                                    '& .MuiAccordionSummary-content': {
                                        margin: '12px 0',
                                        '&.Mui-expanded': {
                                            margin: '12px 0',
                                        },
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                        pr: 2,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <IconCalendar size={20} color='#667eea' />
                                        <Box>
                                            <Typography
                                                variant='subtitle1'
                                                sx={{ fontWeight: 600, color: 'primary.main' }}
                                            >
                                                Workshop Date {index + 1}
                                            </Typography>
                                            {workshop.eventDate && (
                                                <Typography variant='body2' color='text.secondary'>
                                                    {new Date(workshop.eventDate).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                    })}
                                                    {workshop.startTime && workshop.endTime && (
                                                        <>
                                                            {' '}
                                                            • {workshop.startTime} - {workshop.endTime}
                                                        </>
                                                    )}
                                                    {workshop.venue && <> • {workshop.venue}</>}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    <Button
                                        size='small'
                                        color='error'
                                        onClick={e => {
                                            e.stopPropagation();
                                            removeWorkshopDate(index);
                                        }}
                                        sx={{ minWidth: 'auto', px: 1 }}
                                    >
                                        <IconTrash size={16} />
                                    </Button>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 3, background: 'white' }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label='Event Date'
                                            type='date'
                                            value={workshop.eventDate}
                                            onChange={e => updateWorkshopDate(index, 'eventDate', e.target.value)}
                                            error={
                                                !!errors.workshopDates?.[index]?.eventDate ||
                                                !!instantValidation[`${index}_eventDate`]
                                            }
                                            helperText={
                                                errors.workshopDates?.[index]?.eventDate ||
                                                instantValidation[`${index}_eventDate`]
                                            }
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label='Venue'
                                            value={workshop.venue}
                                            onChange={e => updateWorkshopDate(index, 'venue', e.target.value)}
                                            error={!!errors.workshopDates?.[index]?.venue}
                                            helperText={errors.workshopDates?.[index]?.venue}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label='Start Time'
                                            type='time'
                                            value={workshop.startTime}
                                            onChange={e => updateWorkshopDate(index, 'startTime', e.target.value)}
                                            error={!!errors.workshopDates?.[index]?.startTime}
                                            helperText={errors.workshopDates?.[index]?.startTime}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label='End Time'
                                            type='time'
                                            value={workshop.endTime}
                                            onChange={e => updateWorkshopDate(index, 'endTime', e.target.value)}
                                            error={
                                                !!errors.workshopDates?.[index]?.endTime ||
                                                !!instantValidation[`${index}_endTime`]
                                            }
                                            helperText={
                                                errors.workshopDates?.[index]?.endTime ||
                                                instantValidation[`${index}_endTime`]
                                            }
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label='Allowed Number of Registrations'
                                            type='text'
                                            value={
                                                workshop.allowedRegistrations === undefined ||
                                                workshop.allowedRegistrations === null ||
                                                workshop.allowedRegistrations === 0
                                                    ? ''
                                                    : workshop.allowedRegistrations
                                            }
                                            onChange={e => {
                                                const value = e.target.value;
                                                // Allow empty string, otherwise parse as integer
                                                // Handle empty string or whitespace
                                                const trimmedValue = value.trim();
                                                let numericValue = '';
                                                if (trimmedValue !== '') {
                                                    const parsed = parseInt(trimmedValue);
                                                    if (!isNaN(parsed)) {
                                                        numericValue = parsed;
                                                    }
                                                }
                                                updateWorkshopDate(index, 'allowedRegistrations', numericValue);
                                            }}
                                            error={!!errors.workshopDates?.[index]?.allowedRegistrations}
                                            helperText={errors.workshopDates?.[index]?.allowedRegistrations}
                                            inputProps={{
                                                pattern: '[0-9]*',
                                                inputMode: 'numeric',
                                            }}
                                        />
                                    </Grid>

                                    {/* Types Section */}
                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                mt: 2,
                                                p: 2,
                                                border: '1px solid #e0e0e0',
                                                borderRadius: 2,
                                                backgroundColor: '#f8f9fa',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    mb: 2,
                                                }}
                                            >
                                                <Typography
                                                    variant='h6'
                                                    sx={{ fontWeight: 600, color: 'primary.main' }}
                                                >
                                                    🎯 Type-Based Capacity Management
                                                </Typography>
                                                <Button
                                                    size='small'
                                                    variant='outlined'
                                                    startIcon={<IconPlus size={16} />}
                                                    onClick={() => addWorkshopType(index)}
                                                    sx={{ minWidth: 'auto' }}
                                                    title='Add attendee type with specific seat limits (e.g., VIP=10 seats, Standard=20 seats)'
                                                >
                                                    Add Type
                                                </Button>
                                            </Box>

                                            {workshop.types && workshop.types.length > 0 ? (
                                                workshop.types.map((type, typeIndex) => (
                                                    <Box
                                                        key={typeIndex}
                                                        sx={{
                                                            mb: 2,
                                                            p: 2,
                                                            border: '1px solid #ddd',
                                                            borderRadius: 1,
                                                            backgroundColor: 'white',
                                                        }}
                                                    >
                                                        <Grid container spacing={2} alignItems='center'>
                                                            <Grid item xs={12} sm={5}>
                                                                <TextField
                                                                    fullWidth
                                                                    label='Type Name (from upload data)'
                                                                    placeholder='e.g., VIP, Standard, Student'
                                                                    value={type.type_label || ''}
                                                                    onChange={e =>
                                                                        updateWorkshopType(
                                                                            index,
                                                                            typeIndex,
                                                                            'type_label',
                                                                            e.target.value
                                                                        )
                                                                    }
                                                                    size='small'
                                                                    error={
                                                                        !!errors.workshopDates?.[index]?.types?.[
                                                                            `type_${typeIndex}_label`
                                                                        ]
                                                                    }
                                                                    helperText={
                                                                        errors.workshopDates?.[index]?.types?.[
                                                                            `type_${typeIndex}_label`
                                                                        ]
                                                                    }
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} sm={5}>
                                                                <TextField
                                                                    fullWidth
                                                                    label='Max Seats for This Type'
                                                                    placeholder='e.g., 10'
                                                                    type='text'
                                                                    value={type.allowed_registrations || ''}
                                                                    onChange={e => {
                                                                        const value = e.target.value;
                                                                        const trimmedValue = value.trim();
                                                                        let numericValue = '';
                                                                        if (trimmedValue !== '') {
                                                                            const parsed = parseInt(trimmedValue);
                                                                            if (!isNaN(parsed)) {
                                                                                numericValue = parsed;
                                                                            }
                                                                        }
                                                                        updateWorkshopType(
                                                                            index,
                                                                            typeIndex,
                                                                            'allowed_registrations',
                                                                            numericValue
                                                                        );
                                                                    }}
                                                                    size='small'
                                                                    error={
                                                                        !!errors.workshopDates?.[index]?.types?.[
                                                                            `type_${typeIndex}_registrations`
                                                                        ]
                                                                    }
                                                                    helperText={
                                                                        errors.workshopDates?.[index]?.types?.[
                                                                            `type_${typeIndex}_registrations`
                                                                        ]
                                                                    }
                                                                    inputProps={{
                                                                        pattern: '[0-9]*',
                                                                        inputMode: 'numeric',
                                                                    }}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} sm={2}>
                                                                <Button
                                                                    size='small'
                                                                    color='error'
                                                                    onClick={() => removeWorkshopType(index, typeIndex)}
                                                                    sx={{ minWidth: 'auto' }}
                                                                >
                                                                    <IconTrash size={16} />
                                                                </Button>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                ))
                                            ) : (
                                                <Typography
                                                    variant='body2'
                                                    color='text.secondary'
                                                    sx={{ textAlign: 'center', py: 2 }}
                                                >
                                                    <strong>Attendee Types & Capacity Control (Optional):</strong>
                                                    <br />
                                                    Define different attendee types with individual capacity limits. For
                                                    example: Total workshop capacity = 20, Type A = 10 seats, Type B = 5
                                                    seats, Type C = 5 seats. When attendees select this workshop date,
                                                    the system will check if their specific type has available seats. If
                                                    their type is full, this workshop will be disabled for them.
                                                </Typography>
                                            )}

                                            {workshop.types && workshop.types.length > 0 && (
                                                <Box sx={{ mt: 2, p: 1, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                                                    <Typography
                                                        variant='body2'
                                                        color='primary.main'
                                                        sx={{ fontWeight: 500 }}
                                                    >
                                                        📊 Seat Allocation:{' '}
                                                        {workshop.types.reduce(
                                                            (sum, type) =>
                                                                sum + (parseInt(type.allowed_registrations) || 0),
                                                            0
                                                        )}{' '}
                                                        total allocated seats /{' '}
                                                        {workshop.allowedRegistrations || 'Unlimited'} workshop capacity
                                                        <br />
                                                        <small style={{ color: '#666' }}>
                                                            Each type has individual seat limits. When full, that type
                                                            cannot register.
                                                        </small>
                                                    </Typography>
                                                    {errors.workshopDates?.[index]?.types?.sum && (
                                                        <Typography
                                                            variant='body2'
                                                            color='error'
                                                            sx={{ mt: 1, fontWeight: 500 }}
                                                        >
                                                            {errors.workshopDates[index].types.sum}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, background: '#f8f9fa' }}>
                <Button onClick={onClose} variant='outlined'>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant='contained'
                    sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                        },
                    }}
                >
                    {theme ? 'Update Theme' : 'Create Theme'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Main Component
const ManageTheme = () => {
    const { eventId, role } = useParams();
    const [modalOpen, setModalOpen] = useState(false);
    const [editingTheme, setEditingTheme] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const { data, loading, error, refetch } = useQuery(GET_THEMES, {
        variables: { eventId },
        errorPolicy: 'all',
    });

    const [createTheme] = useMutation(CREATE_THEME);
    const [updateTheme] = useMutation(UPDATE_THEME);
    const [deleteTheme] = useMutation(DELETE_THEME);

    const themes = data?.themes || [];

    const handleCreateTheme = () => {
        setEditingTheme(null);
        setModalOpen(true);
    };

    const handleEditTheme = theme => {
        setEditingTheme(theme);
        setModalOpen(true);
    };

    const handleViewTheme = theme => {
        // Navigate to theme detail page
        window.location.href = `/${eventId}/${role}/manage-theme/${theme.id}`;
    };

    const handleSaveTheme = async formData => {
        try {
            if (editingTheme) {
                // Clean workshop dates data before sending (preserve existing IDs)
                const cleanWorkshopDates = formData.workshopDates.map(date => ({
                    id: date.id, // Preserve existing workshop date ID
                    eventDate: date.eventDate,
                    startTime: date.startTime,
                    endTime: date.endTime,
                    venue: date.venue,
                    allowedRegistrations: date.allowedRegistrations,
                    types: (date.types || []).map(type => ({
                        type_label: type.type_label,
                        allowed_registrations: type.allowed_registrations,
                    })), // Clean types array - remove __typename
                }));

                await updateTheme({
                    variables: {
                        id: editingTheme.id,
                        input: {
                            ...formData,
                            workshopDates: cleanWorkshopDates,
                            eventId,
                        },
                    },
                });
                setSnackbar({
                    open: true,
                    message: 'Theme updated successfully!',
                    severity: 'success',
                });
            } else {
                // Clean workshop dates data before sending (remove extra fields)
                const cleanWorkshopDates = formData.workshopDates.map(date => ({
                    eventDate: date.eventDate,
                    startTime: date.startTime,
                    endTime: date.endTime,
                    venue: date.venue,
                    allowedRegistrations: date.allowedRegistrations,
                    types: (date.types || []).map(type => ({
                        type_label: type.type_label,
                        allowed_registrations: type.allowed_registrations,
                    })), // Clean types array - remove __typename
                }));

                await createTheme({
                    variables: {
                        input: {
                            ...formData,
                            workshopDates: cleanWorkshopDates,
                            eventId,
                        },
                    },
                });
                setSnackbar({
                    open: true,
                    message: 'Theme created successfully!',
                    severity: 'success',
                });
            }
            setModalOpen(false);
            refetch();
        } catch (error) {
            setSnackbar({
                open: true,
                message: `Error: ${error.message}`,
                severity: 'error',
            });
        }
    };

    const handleDeleteTheme = async (themeId, reason) => {
        try {
            await deleteTheme({
                variables: {
                    id: themeId,
                    reason,
                    eventId,
                },
            });
            setSnackbar({
                open: true,
                message: 'Theme deleted successfully!',
                severity: 'success',
            });
            refetch();
        } catch (error) {
            setSnackbar({
                open: true,
                message: `Error: ${error.message}`,
                severity: 'error',
            });
        }
    };

    if (loading) {
        return (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity='error' sx={{ mt: 2 }}>
                Error loading themes: {error.message}
            </Alert>
        );
    }

    return (
        <>
            <Helmet>
                <title>Manage Themes - Eventegration</title>
            </Helmet>

            <MainCard>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                        <Typography variant='h4' gutterBottom>
                            Manage Themes
                        </Typography>
                        <Typography variant='body1' color='text.secondary'>
                            Create and manage event themes with workshop schedules
                        </Typography>
                    </Box>
                    <AnimateButton>
                        <Button
                            variant='contained'
                            startIcon={<IconPlus size={20} />}
                            onClick={handleCreateTheme}
                            size='large'
                        >
                            Create New Theme
                        </Button>
                    </AnimateButton>
                </Box>

                {themes.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <IconPalette size={80} style={{ color: '#ccc', marginBottom: 24 }} />
                        <Typography variant='h5' gutterBottom color='text.secondary'>
                            No themes created yet
                        </Typography>
                        <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
                            Create your first theme to start managing workshop schedules and registrations.
                        </Typography>
                        <Button
                            variant='contained'
                            startIcon={<IconPlus size={20} />}
                            onClick={handleCreateTheme}
                            size='large'
                        >
                            Create First Theme
                        </Button>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {themes.map(theme => (
                            <Grid item xs={12} sm={6} md={4} key={theme.id}>
                                <ThemeCard
                                    theme={theme}
                                    onEdit={handleEditTheme}
                                    onDelete={handleDeleteTheme}
                                    onView={handleViewTheme}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}

                <ThemeModal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    theme={editingTheme}
                    onSave={handleSaveTheme}
                />

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </MainCard>
        </>
    );
};

export default ManageTheme;
